/* Client wrapper for the worker's AI draft-assist. Degrades gracefully:
   returns { ok:false } when the endpoint is unconfigured or unreachable. */

export interface DraftBriefResult {
  ok: boolean;
  reason?: string;
  sections?: { title: string; body: string }[];
  spaces?: { name: string; qty: number; area: number; zone?: string; note?: string }[];
}

export interface DraftOpts {
  instruction?: string;   // studio guidance to steer the draft
  notes?: string;         // client-provided brief/notes to extract from
  only?: string;          // draft a single section by title (inline)
  fileData?: string;      // an uploaded PDF/image data URI to read the brief from
}

export interface SwatchResult { ok: boolean; reason?: string; swatches?: { name: string; hex: string; material?: string }[]; }
export async function moodSwatches(type: string, images: string[]): Promise<SwatchResult> {
  try {
    const r = await fetch("/api/mood-swatches", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ type, images }) });
    return (await r.json()) as SwatchResult;
  } catch { return { ok: false, reason: "unreachable" }; }
}

export interface SummaryResult { ok: boolean; reason?: string; summary?: string; }
export async function summarizeReview(title: string, sheets: any[]): Promise<SummaryResult> {
  try {
    const r = await fetch("/api/summarize-review", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ title, sheets }) });
    return (await r.json()) as SummaryResult;
  } catch { return { ok: false, reason: "unreachable" }; }
}

export async function draftBrief(
  project: { name: string; type: string; types?: string[]; location: string; area?: string; gia?: number; giaUnit?: string; budget?: number },
  sections: { title: string; body?: string }[],
  opts: DraftOpts = {},
): Promise<DraftBriefResult> {
  try {
    const r = await fetch("/api/draft-brief", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ project, sections, ...opts }),
    });
    return (await r.json()) as DraftBriefResult;
  } catch {
    return { ok: false, reason: "unreachable" };
  }
}

/* ---- drawing-set QA, project copilot, and AI take-off (all worker-backed) ---- */
export interface QaFindingWire { severity: "high" | "medium" | "low"; category: string; sheet: string | null; finding: string; suggestion: string | null }
export async function qaDrawings(project: string, sheets: { code: string; title: string; discipline: string; phase: string; hasDrawing: boolean; building?: string }[]): Promise<{ ok: boolean; reason?: string; summary?: string; findings?: QaFindingWire[] }> {
  try {
    const r = await fetch("/api/qa-drawings", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ project, sheets }) });
    return await r.json();
  } catch { return { ok: false, reason: "unreachable" }; }
}
export async function copilotAsk(question: string, context: string): Promise<{ ok: boolean; reason?: string; answer?: string }> {
  try {
    const r = await fetch("/api/copilot", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ question, context }) });
    return await r.json();
  } catch { return { ok: false, reason: "unreachable" }; }
}
export interface TakeoffItemWire { code: string | null; discipline: string | null; description: string; qty: number; unit: string; note: string | null }
export async function aiTakeoff(sheets: { code: string; title: string; discipline: string; image: string }[]): Promise<{ ok: boolean; reason?: string; items?: TakeoffItemWire[] }> {
  try {
    const r = await fetch("/api/takeoff", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sheets }) });
    return await r.json();
  } catch { return { ok: false, reason: "unreachable" }; }
}

/* ---- scope sentinel, as-built deviation, studio knowledge base ---- */
export interface ScopeFlagWire { index: number; verdict: "in-scope" | "out-of-scope" | "borderline"; reason: string; suggestedTitle: string | null; suggestedAmount: number | null }
export async function scopeCheck(scope: string, requests: { source: string; ref?: string; text: string }[], currency: string): Promise<{ ok: boolean; reason?: string; flags?: ScopeFlagWire[] }> {
  try {
    const r = await fetch("/api/scope-check", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scope, requests, currency }) });
    return await r.json();
  } catch { return { ok: false, reason: "unreachable" }; }
}

export interface DeviationWire { severity: "high" | "medium" | "low"; element: string; observed: string; drawn: string; confidence: "high" | "medium" | "low" }
export async function asbuiltCheck(drawing: string, photo: string, sheet?: string, note?: string): Promise<{ ok: boolean; reason?: string; summary?: string; deviations?: DeviationWire[] }> {
  try {
    const r = await fetch("/api/asbuilt-check", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ drawing, photo, sheet, note }) });
    return await r.json();
  } catch { return { ok: false, reason: "unreachable" }; }
}

export async function knowledgeAsk(question: string, corpus: string): Promise<{ ok: boolean; reason?: string; answer?: string }> {
  try {
    const r = await fetch("/api/knowledge", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ question, corpus }) });
    return await r.json();
  } catch { return { ok: false, reason: "unreachable" }; }
}

/* ---- the daily project agent ---- */
export interface AgentActionWire { kind: "email" | "chase" | "flag" | "task"; title: string; detail: string; to: string | null; draft: string | null; urgency: "high" | "medium" | "low" }
export async function runAgent(context: string, studio: string, client: string): Promise<{ ok: boolean; reason?: string; summary?: string; actions?: AgentActionWire[] }> {
  try {
    const r = await fetch("/api/agent", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ context, studio, client }) });
    return await r.json();
  } catch { return { ok: false, reason: "unreachable" }; }
}

/* ---- the concept presentation ---- */
export interface DraftedSlide { kind: "statement" | "image" | "spaces" | "palette" | "closing"; title: string; body: string; sourceQuote?: string | null; sourceWhere?: string | null; useBoard?: number | null; useCell?: number | null }
export interface DraftedPanel { board: number; cell: number; kind: string; caption: string; worthShowing: boolean; optionRef?: string | null }
export interface DraftedOption {
  ref: string; name: string | null; thesis: string; description: string;
  strengths: string[]; tradeoffs: string[]; costNote: string | null;
  /* present mode — read off the boards, not invented */
  boardRef?: string | null; page?: number | null;
  metrics?: { label: string; value: string }[];
  decisions?: string[];
}
export interface DraftedSource { quote: string; where?: string | null; topic?: string; usedFor?: string | null; file?: string | null }
/** `files` are data URIs — a PDF proposal, a scanned brief, a photographed page.
 *  The worker turns them into document/image blocks so the model reads the paper
 *  the client actually sent. */
export async function draftConcept(
  context: string,
  opts: {
    options?: number; instruction?: string; studio?: string;
    /** "present" reads options off the boards; "design" writes the proposal for a
     *  resolved scheme; "develop" helps shape one that doesn't exist yet. */
    mode?: "present" | "develop" | "design";
    files?: { name: string; dataUri: string; page?: number; label?: string }[];
  } = {},
): Promise<{ ok: boolean; reason?: string; title?: string; subtitle?: string | null; slides?: DraftedSlide[]; options?: DraftedOption[]; sources?: DraftedSource[]; panels?: DraftedPanel[]; ignored?: string | null; readFiles?: string[] }> {
  try {
    const r = await fetch("/api/draft-concept", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ context, ...opts }) });
    return await r.json();
  } catch { return { ok: false, reason: "unreachable" }; }
}
export async function rewriteSlide(slide: { title: string; body: string }, instruction: string, context?: string): Promise<{ ok: boolean; reason?: string; title?: string; body?: string }> {
  try {
    const r = await fetch("/api/rewrite-slide", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...slide, instruction, context }) });
    return await r.json();
  } catch { return { ok: false, reason: "unreachable" }; }
}
